import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Avatar, Box, Breadcrumbs, Link, Paper, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';

import { getCurrentUser, getNutritionistProfilePreferences } from '@/lib/api/auth';
import { cn } from '@/lib/utils';
import availabilityIcon from '@/assets/images/settings/availability.svg';
import myCalorieSciencePlanIcon from '@/assets/images/settings/my-caloriescience-plan.svg';
import notificationsIcon from '@/assets/images/settings/notifications.svg';
import paymentMethodsIcon from '@/assets/images/settings/payment-methods.svg';
import plansPackagesIcon from '@/assets/images/settings/plans-packages.svg';
import practiceInfoIcon from '@/assets/images/settings/practice-info.svg';
import savedLibraryIcon from '@/assets/images/settings/saved-library.svg';
import securityIcon from '@/assets/images/settings/security.svg';
import invoiceTaxDetailsIcon from '@/assets/images/settings/invoice-tax-details.svg';
import layer1Graphic from '@/assets/images/layer-1.svg';

type ProfileTab = 'settings' | 'availability' | 'billing' | 'feedback';

interface SettingsHubProps {
  onOpenTab: (tab: ProfileTab) => void;
  onOpenProfileDetails: () => void;
  onOpenNotificationPreferences: () => void;
  onOpenSecurity: () => void;
  onNavigateInvoicing: () => void;
  onOpenPlansPackages: () => void;
  onOpenSubscriptionPlan: () => void;
  onNavigateRecipes: () => void;
}

interface SettingsOptionRowProps {
  title: string;
  description: string;
  iconSrc: string;
  iconContainerClassName: string;
  demoId?: string;
  meta?: string;
  onClick?: () => void;
  isFirst?: boolean;
}

const getInitials = (name: string): string => {
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 0) return 'VD';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;

const readFirstString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const resolveProfessionalQualification = (payload: unknown): string => {
  const root = asRecord(payload);
  if (!root) return '';

  const data = asRecord(root.data) ?? root;
  const professionalDetails =
    asRecord(data.professionalDetails) ??
    asRecord(data.professional) ??
    asRecord(data.credentialsAndSpecialisations);
  const credentials = asRecord(data.credentials);

  return readFirstString(
    professionalDetails?.primaryQualification,
    professionalDetails?.qualification,
    credentials?.primaryQualification,
    credentials?.qualification,
    data.primaryQualification,
    data.qualification,
    data.professionalQualification,
  );
};

const SettingsOptionRow: React.FC<SettingsOptionRowProps> = ({
  title,
  description,
  iconSrc,
  iconContainerClassName,
  demoId,
  meta,
  onClick,
  isFirst = false,
}) => {
  const rowClassName = cn(
    'flex w-full items-center justify-between gap-4 px-4 py-3.5 text-left transition-colors sm:px-5',
    !isFirst && 'border-t border-[#EDF2F6]',
    onClick ? 'hover:bg-[#F8FBFD]' : 'cursor-default'
  );

  const rowContent = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        <span className={cn('inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', iconContainerClassName)}>
          <img src={iconSrc} alt="" aria-hidden className="h-5 w-5" />
        </span>

        <span className="min-w-0">
          <span className="block truncate text-[16px] font-semibold text-[#111A22] sm:text-[17px]">{title}</span>
          <span className="block truncate text-[13px] font-semibold text-[#6E7E89] sm:text-[14px]">{description}</span>
        </span>
      </div>

      <span className="flex shrink-0 items-center gap-2 text-[#8896A4]">
        {meta ? <span className="text-[13px] font-semibold sm:text-[14px]">{meta}</span> : null}
        <ChevronRight className="h-4 w-4" />
      </span>
    </>
  );

  if (!onClick) {
    return <div className={rowClassName}>{rowContent}</div>;
  }

  return (
    <button type="button" data-demo-id={demoId} onClick={onClick} className={rowClassName}>
      {rowContent}
    </button>
  );
};

const PracticeInfoOption: React.FC<{ onClick: () => void; isFirst?: boolean }> = ({ onClick, isFirst }) => (
  <SettingsOptionRow
    isFirst={isFirst}
    title="Profile and Practice Information"
    description="Personal details and clinic information"
    iconSrc={practiceInfoIcon}
    iconContainerClassName="bg-[#E8F8F4]"
    demoId="settings-practice-profile"
    onClick={onClick}
  />
);

const AvailabilityOption: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <SettingsOptionRow
    title="Availability"
    description="Working hours, breaks, and blocked dates"
    iconSrc={availabilityIcon}
    iconContainerClassName="bg-[#E9F2FF]"
    demoId="settings-availability"
    onClick={onClick}
  />
);

const PaymentMethodsOption: React.FC<{ onClick: () => void; isFirst?: boolean }> = ({ onClick, isFirst }) => (
  <SettingsOptionRow
    isFirst={isFirst}
    title="Payment Methods"
    description="Accepted channels and payout accounts"
    iconSrc={paymentMethodsIcon}
    iconContainerClassName="bg-[#E8FBF6]"
    onClick={onClick}
  />
);

const InvoiceTaxDetailsOption: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <SettingsOptionRow
    title="Invoice and Tax Details"
    description="Manage invoice settings, defaults, tax registration, and business details"
    iconSrc={invoiceTaxDetailsIcon}
    iconContainerClassName="bg-[#FFF3E8]"
    demoId="settings-invoice-tax"
    onClick={onClick}
  />
);

const NotificationsOption: React.FC<{ onClick: () => void; isFirst?: boolean }> = ({ onClick, isFirst }) => (
  <SettingsOptionRow
    isFirst={isFirst}
    title="Notification Preferences"
    description="Choose events and delivery channels"
    iconSrc={notificationsIcon}
    iconContainerClassName="bg-[#EAF9F6]"
    onClick={onClick}
  />
);

const SecurityOption: React.FC<{ onClick: () => void; isFirst?: boolean }> = ({ onClick, isFirst }) => (
  <SettingsOptionRow
    isFirst={isFirst}
    title="Security"
    description="Password and two-factor authentication"
    iconSrc={securityIcon}
    iconContainerClassName="bg-[#EAF0FB]"
    demoId="settings-security"
    onClick={onClick}
  />
);

const PlansPackagesOption: React.FC<{ onClick: () => void; isFirst?: boolean }> = ({ onClick, isFirst }) => (
  <SettingsOptionRow
    isFirst={isFirst}
    title="Packages and Plans"
    description="What you offer to clients: consultation types, multi-session plans, and custom packages"
    iconSrc={plansPackagesIcon}
    iconContainerClassName="bg-[#FFF3E8]"
    demoId="settings-packages"
    onClick={onClick}
  />
);

const MyPlanOption: React.FC<{ onClick: () => void; isFirst?: boolean }> = ({ onClick, isFirst }) => (
  <SettingsOptionRow
    isFirst={isFirst}
    title="My CalorieScience Plan"
    description="Usage, feature limits, and billing for your CalorieScience subscription"
    iconSrc={myCalorieSciencePlanIcon}
    iconContainerClassName="bg-[#E7F8F8]"
    onClick={onClick}
  />
);

const SavedLibraryOption: React.FC<{ onClick: () => void; isFirst?: boolean }> = ({ onClick, isFirst }) => (
  <SettingsOptionRow
    isFirst={isFirst}
    title="Saved library"
    description="View and manage recipes and insights you've saved from across the platform"
    iconSrc={savedLibraryIcon}
    iconContainerClassName="bg-[#EAF3FF]"
    onClick={onClick}
  />
);

const SettingsSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="px-1 text-xs font-bold uppercase tracking-[0.14em] text-[#1F2B34]">{title}</h2>
    <div className="overflow-hidden rounded-2xl bg-white">{children}</div>
  </section>
);

const SettingsHub: React.FC<SettingsHubProps> = ({
  onOpenTab,
  onOpenProfileDetails,
  onOpenNotificationPreferences,
  onOpenSecurity,
  onNavigateInvoicing,
  onOpenPlansPackages,
  onOpenSubscriptionPlan,
  onNavigateRecipes,
}) => {
  const currentUser = getCurrentUser();
  const userName = currentUser?.fullName?.trim() || currentUser?.email?.split('@')?.[0] || 'User';
  const profilePreferencesQuery = useQuery({
    queryKey: ['nutritionist', 'profile-preferences', 'settings-hub', currentUser?.id ?? 'anonymous'],
    queryFn: getNutritionistProfilePreferences,
    enabled: Boolean(currentUser?.token),
    staleTime: 5 * 60 * 1000,
  });
  const professionalQualification =
    resolveProfessionalQualification(profilePreferencesQuery.data) ||
    resolveProfessionalQualification(currentUser);
  const userSubtitle = professionalQualification || 'Professional qualification not set';
  const initials = useMemo(() => getInitials(userName), [userName]);

  return (
    <Box data-demo-id="settings-hub" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#f7f9fb',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: '1px solid #dbe6eb',
              bgcolor: '#f8fafc',
              px: { xs: 2, sm: 3 },
              py: 1.25,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Settings
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <div className="space-y-6 px-4 pb-6 pt-4 sm:px-6 lg:px-7 lg:pb-8 lg:pt-6">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#18B5DF] to-[#1A92C6] px-5 py-5 sm:px-7">
            <div className="pointer-events-none absolute inset-0 z-0 flex items-stretch justify-end opacity-90">
              <img
                src={layer1Graphic}
                alt=""
                aria-hidden
                className="h-full w-[220px] object-cover object-right sm:w-[260px] md:w-[297px]"
              />
            </div>
            <div className="relative flex min-w-0 items-center gap-4">
              <Avatar
                src={currentUser?.profileImageUrl || undefined}
                alt={userName}
                sx={{
                  width: 56,
                  height: 56,
                  border: '2px solid rgba(255,255,255,0.6)',
                  bgcolor: '#fff',
                  color: '#1C99C9',
                  fontWeight: 700,
                }}
              >
                {initials}
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-2xl font-semibold leading-tight text-white sm:text-[32px]">{userName}</p>
                <p className="text-sm text-white/85 sm:text-[15px]">{userSubtitle}</p>
              </div>
            </div>
          </div>

          <SettingsSection title="Practice">
            <PracticeInfoOption isFirst onClick={onOpenProfileDetails} />
            <AvailabilityOption onClick={() => onOpenTab('availability')} />
          </SettingsSection>

          <SettingsSection title="Billing">
            {/* Hidden for now; keep Payment Methods row for later use */}
            {/* <PaymentMethodsOption isFirst onClick={() => onOpenTab('billing')} /> */}
            <InvoiceTaxDetailsOption onClick={onNavigateInvoicing} />
          </SettingsSection>

          <SettingsSection title="Preferences">
            {/* Hidden for now; keep Notification Preferences row for later use */}
            {/* <NotificationsOption isFirst onClick={onOpenNotificationPreferences} /> */}
            <SecurityOption isFirst onClick={onOpenSecurity} />
          </SettingsSection>

          <SettingsSection title="Client Packages">
            <PlansPackagesOption isFirst onClick={onOpenPlansPackages} />
          </SettingsSection>

          {/* Hidden for now; keep CalorieScience Platform section for later use */}
          {/*
          <SettingsSection title="CalorieScience Platform">
            <MyPlanOption isFirst onClick={onOpenSubscriptionPlan} />
          </SettingsSection>
          */}

          {/* Hidden for now; keep Your Saved Library section for later use */}
          {/*
          <SettingsSection title="Your Saved Library">
            <SavedLibraryOption isFirst onClick={onNavigateRecipes} />
          </SettingsSection>
          */}
        </div>
      </Paper>
    </Box>
  );
};

export default SettingsHub;
